import {
  ErrorDialogContext,
  errorHandledAsync,
} from "@/shared/context/error-context";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { useContext, useRef } from "react";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { usePostFinanceAccount } from "@/features/accounts/finance/hooks/use-post-accounts";
import { useGetFinanceAccount } from "@/features/accounts/finance/hooks/use-get-accounts";
import { usePostCrecaAccount } from "@/features/accounts/creca/hooks/use-post-accounts";
import { useGetCrecaAccount } from "@/features/accounts/creca/hooks/use-get-accounts";
import { useGetPaymentAccounts } from "@/features/accounts/payment/scraping/hooks/use-get-accounts";
import { useGetEcshopAccount } from "@/features/accounts/ecshop/hooks/use-get-accounts";
import {
  useAccountGroupsContext,
  Accounts,
} from "@/features/accounts/account-group/contexts/account-groups-context";
import { useHomeStates } from "@/features/home/hooks/use-home-states";
import { useGetCsvExcelAccount } from "@/features/accounts/csv-excel/hooks/use-get-accounts";
import { useGetOcrAccount } from "@/features/accounts/ocr/hooks/use-get-accounts";
import { AccountsInfo } from "@/features/accounts/shared/hooks/use-select-accounts";
import { useInstitutionsServices } from "@/features/institutions/hooks/use-institutions-services";
import { useGetScrapFinanceAccount } from "@/features/accounts/scrap-finance/hooks/use-get-accounts";

export const useHome = () => {
  const { openModal, setOpenModal } = useHomeStates();
  const { getFinanceAccounts } = useGetFinanceAccount();
  const { registerFinanceAccounts } = usePostFinanceAccount();

  const { getCrecaAccounts } = useGetCrecaAccount();
  const { registerCrecaAccounts } = usePostCrecaAccount();

  const { getPaymentAccounts } = useGetPaymentAccounts();

  const { getEcshopAccounts } = useGetEcshopAccount();
  const { getCsvExcelAccounts } = useGetCsvExcelAccount();

  const { getOcrAccounts } = useGetOcrAccount();
  const { getScrapFinanceAccounts } = useGetScrapFinanceAccount();

  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  const { selectedAccountGroup, setAccounts } = useAccountGroupsContext();
  const accountGroupId = selectedAccountGroup.accountGroupId;

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);
  // 口座一覧取得管理ID
  const latestGetAllAccountsRequestId = useRef(0);

  // 表示する口座リスト情報を取得
  const getAccountsToDisplay = async () => {
    if (accountGroupId === "") {
      setAccounts([]);
    }
  };

  // 銀行口座登録（認可完了後の処理）
  const registerFinances = async (
    accountGroupId: string,
    financeAccountId: string,
  ): Promise<AccountsInfo[] | undefined> => {
    loadingCtx.setIsLoading(true);
    try {
      const registerResult = await registerFinanceAccounts(accountGroupId, financeAccountId);
      // エラー時（undefined）は処理を中断
      if (registerResult === undefined) {
        return undefined;
      }

      const res = await getFinanceAccounts(accountGroupId, financeAccountId);
      // エラー時（undefined）は処理を中断
      if (res === undefined) {
        return undefined;
      }

      const resAccounts: AccountsInfo[] = res.flatMap((financeAccount) =>
        financeAccount.finance_list
          .filter((finance) => finance.finance_type_code === 1 || finance.finance_type_code === 2) // 普通・当座のみ
          .map((finance) => ({
            accountGroupId: accountGroupId,
            mlinkAccountId: financeAccount.finance_account_id,
            mlinkId: finance.finance_id,
            accountKindName: finance.account_display_name,
            accountNo: finance.finance_number,
            selectedStatus: finance.selected_status,
            //accountType: "finance",
            passbookType: financeAccount.industry_code,
            userInputName: "",
            scrapeType: 0,
            otpYn: "0", // 銀行口座はワンパス非対応のため0
          })),
      );
      return resAccounts;
    } catch (error) {
      // エラーハンドリング
      console.log("registerFinances:", error);
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // クレカ登録（認可完了後の処理）
  const registerCrecas = async (
    accountGroupId: string,
    crecaAccountId: string,
  ): Promise<AccountsInfo[] | undefined> => {
    loadingCtx.setIsLoading(true);
    try {
      const registerResult = await registerCrecaAccounts(accountGroupId, crecaAccountId);
      // エラー時（undefined）は処理を中断
      if (registerResult === undefined) {
        return undefined;
      }

      const res = await getCrecaAccounts(accountGroupId, crecaAccountId);
      // エラー時（undefined）は処理を中断
      if (res === undefined) {
        return undefined;
      }

      const resAccounts: AccountsInfo[] = res.map((creca) => ({
        accountGroupId: accountGroupId,
        mlinkAccountId: creca.creca_account_id,
        mlinkId: "",
        accountKindName: creca.creca_name,
        accountNo: creca.account_no,
        selectedStatus: creca.selected_status,
        //accountType: "creca",
        passbookType: creca.company_code,
        userInputName: "",
        scrapeType: 0,
        otpYn: "0", // クレカはワンパス非対応のため0
      }));
      return resAccounts;
    } catch (error) {
      // エラーハンドリング
      console.log("registerCrecas:", error);
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // Look口座登録（サーバ版スクレイピング）
  const registerPayments = async (
    accountGroupId: string,
    paymentAccountId: string,
  ): Promise<AccountsInfo[] | undefined> => {
    loadingCtx.setIsLoading(true);
    try {
      const res = await getPaymentAccounts(
        accountGroupId,
        paymentAccountId,
        undefined, // industryCode
      );
      // エラー時（undefined）は処理を中断
      if (res === undefined) {
        return undefined;
      }

      const resAccounts: AccountsInfo[] = res.flatMap((paymentAccount) =>
        paymentAccount.goods_list.map((goods) => ({
          accountGroupId: accountGroupId,
          mlinkAccountId: paymentAccount.payment_account_id,
          mlinkId: goods.payment_id,
          accountKindName: goods.account_kind_name,
          accountNo: goods.account_no,
          selectedStatus: 0,
          //accountType: "payment",
          passbookType: paymentAccount.industry_code,
          userInputName: "",
          scrapeType: paymentAccount.scrape_type,
          otpYn: goods.otp_yn, // スクレイピングはワンパス発生有無を検知
        })),
      );
      return resAccounts;
    } catch (error) {
      // エラーハンドリング
      console.log("registerPayments:", error);
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // その他連携（ECショップ）口座登録（認可完了後の処理）
  const registerEcshops = async (
    accountGroupId: string,
    ecshopAccountId: string,
  ): Promise<AccountsInfo[] | undefined> => {
    loadingCtx.setIsLoading(true);
    try {
      const res = await getEcshopAccounts(accountGroupId, ecshopAccountId);
      // エラー時（undefined）は処理を中断
      if (res === undefined) {
        return undefined;
      }

      const resAccounts: AccountsInfo[] = res.flatMap((ecshopAccount) =>
        ecshopAccount.shop_list.map((ecshop) => ({
          accountGroupId: ecshopAccount.account_group_id,
          mlinkAccountId: ecshopAccount.ecshop_account_id,
          mlinkId: ecshop.ecshop_id,
          accountKindName: ecshop.shop_title,
          accountNo: ecshop.shop_id,
          selectedStatus: ecshop.selected_status,
          //accountType: "finance",
          passbookType: ecshopAccount.industry_code,
          userInputName: "",
          scrapeType: 0,
          otpYn: "0", // ECショップはワンパス非対応のため0
        })),
      );
      return resAccounts;
    } catch (error) {
      // エラーハンドリング
      console.log("registerEcshops:", error);
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  const getFinances = async (accountGroupId: string): Promise<Accounts[] | undefined> => {
    const financeAccounts = await getFinanceAccounts(
      accountGroupId,
      undefined, // financeAccountId
      true, // excludeUnselected
    );
    if (financeAccounts !== undefined) {
      const accounts = financeAccounts.flatMap((financeAccount) =>
        financeAccount.finance_list
          .filter((finance) => finance.finance_type_code === 1 || finance.finance_type_code === 2) // 普通・当座のみ
          .map((finance) => ({
            industryCode: financeAccount.industry_code,
            companyCode: financeAccount.company_code,
            companyName: "", //financeAccount.company_name,
            serviceCode: financeAccount.service_code,
            serviceName: "", //financeAccount.service_name,
            branchCode: financeAccount.branch_code,
            branchName: "", //financeAccount.branch_name,
            passbookName: finance.passbook_name,
            balance: finance.last_balance !== null ? finance.last_balance.split(".")[0] : "", // Decimalのため小数点以下切り捨て
            authorizationStatus: finance.authorization_status,
            selectedStatus: finance.selected_status,
            accountGroupId: accountGroupId,
            mlinkAccountId: financeAccount.finance_account_id,
            mlinkId: finance.finance_id,
            passbookType: "01",
            registeredDate: finance.registered_date,
            scrapeType: 0,
            ocrProcessId: "",
            ocrProcessStatus: 0,
            otpYn: "0", // 銀行口座はワンパス非対応のため0
            isUnderMaintenance: false,
          })),
      );
      return accounts.filter((account) => account.selectedStatus === 1);
    }
    return undefined;
  };

  const getCrecas = async (accountGroupId: string): Promise<Accounts[] | undefined> => {
    const crecaAccounts = await getCrecaAccounts(
      accountGroupId,
      undefined, // crecaAccountId
      true, // excludeUnselected
    );
    if (crecaAccounts !== undefined) {
      const accounts = crecaAccounts.map((crecaAccount) => ({
        industryCode: crecaAccount.industry_code,
        companyCode: crecaAccount.company_code,
        companyName: "", // crecaAccount.company_name,
        serviceCode: crecaAccount.service_code,
        serviceName: "", // crecaAccount.service_name,
        branchCode: "",
        branchName: "",
        passbookName: crecaAccount.passbook_name,
        balance: "",
        authorizationStatus: crecaAccount.authorization_status,
        selectedStatus: crecaAccount.selected_status,
        accountGroupId: accountGroupId,
        mlinkAccountId: crecaAccount.creca_account_id,
        mlinkId: "",
        passbookType: crecaAccount.company_code,
        registeredDate: crecaAccount.registered_date,
        scrapeType: 0,
        ocrProcessId: "",
        ocrProcessStatus: 0,
        otpYn: "0", // クレカはワンパス非対応のため0
        isUnderMaintenance: false,
      }));
      return accounts.filter((account) => account.selectedStatus === 1);
    }
    return undefined;
  };

  const getPayments = async (
    accountGroupId: string,
    industryCode: string,
  ): Promise<Accounts[] | undefined> => {
    const paymentAccounts = await getPaymentAccounts(
      accountGroupId,
      undefined, // paymentAccountId
      industryCode,
    );
    if (paymentAccounts !== undefined) {
      const accounts = paymentAccounts.flatMap((paymentAccount) =>
        paymentAccount.goods_list.map((goods) => ({
          industryCode: paymentAccount.industry_code,
          companyCode: paymentAccount.company_code,
          companyName: paymentAccount.company_name,
          serviceCode: paymentAccount.service_code,
          serviceName: paymentAccount.service_name,
          branchCode: "",
          branchName: "",
          passbookName: goods.passbook_name,
          balance: goods.balance,
          authorizationStatus: goods.authorization_status,
          selectedStatus: 1,
          accountGroupId: accountGroupId,
          mlinkAccountId: paymentAccount.payment_account_id,
          mlinkId: goods.payment_id,
          passbookType: industryCode,
          registeredDate: goods.registered_date,
          scrapeType: paymentAccount.scrape_type,
          ocrProcessId: "",
          ocrProcessStatus: 0,
          otpYn: goods.otp_yn,
          isUnderMaintenance: false,
        })),
      );
      return accounts.filter((account) => account.selectedStatus === 1);
    }
    return undefined;
  };

  const getEcshops = async (accountGroupId: string): Promise<Accounts[] | undefined> => {
    const ecshopAccounts = await getEcshopAccounts(
      accountGroupId,
      undefined, // ecshopAccountId
      true, // excludeUnselected
    );
    if (ecshopAccounts !== undefined) {
      const accounts = ecshopAccounts.flatMap((ecshopAccount) =>
        ecshopAccount.shop_list.map((ecshop) => ({
          industryCode: ecshopAccount.industry_code,
          companyCode: ecshopAccount.company_code,
          companyName: "", //ecshopAccount.company_name,
          serviceCode: ecshopAccount.service_code,
          serviceName: "", //ecshopAccount.service_name,
          branchCode: ecshopAccount.branch_code,
          branchName: "", //ecshopAccount.branch_name,
          passbookName: ecshop.input_shop_title,
          balance: "",
          authorizationStatus: 1,
          selectedStatus: ecshop.selected_status,
          accountGroupId: accountGroupId,
          mlinkAccountId: ecshopAccount.ecshop_account_id,
          mlinkId: ecshop.ecshop_id,
          passbookType: "20",
          registeredDate: ecshop.registered_date,
          scrapeType: 0,
          ocrProcessId: "",
          ocrProcessStatus: 0,
          otpYn: "0", // ECショップはワンパス非対応のため0
          isUnderMaintenance: false,
        })),
      );
      return accounts.filter((account) => account.selectedStatus === 1);
    }
    return undefined;
  };

  const getCsvExcels = async (accountGroupId: string): Promise<Accounts[] | undefined> => {
    const csvExcelAccounts = await getCsvExcelAccounts(accountGroupId);
    if (csvExcelAccounts !== undefined) {
      const accounts = csvExcelAccounts.map((csvExcelAccount) => ({
        industryCode: "",
        companyCode: csvExcelAccount.company_code,
        companyName: "",
        serviceCode: "",
        serviceName: "",
        branchCode: "",
        branchName: "",
        passbookName: csvExcelAccount.passbook_name,
        balance: csvExcelAccount.balance,
        authorizationStatus: 1,
        selectedStatus: 1,
        accountGroupId: accountGroupId,
        mlinkAccountId: csvExcelAccount.csvexcel_account_id,
        mlinkId: "",
        passbookType: csvExcelAccount.account_type === 1 ? "201" : "202",
        registeredDate: csvExcelAccount.registered_at,
        scrapeType: 0,
        ocrProcessId: "",
        ocrProcessStatus: 0,
        otpYn: "0", // CSV/Excelはワンパス非対応のため0
        isUnderMaintenance: false,
      }));
      return accounts.filter((account) => account.selectedStatus === 1);
    }
    return undefined;
  };

  const getOcrs = async (accountGroupId: string): Promise<Accounts[] | undefined> => {
    const ocrAccounts = await getOcrAccounts(accountGroupId);
    if (ocrAccounts !== undefined) {
      const accounts = ocrAccounts.flatMap((ocrAccount) => ({
        industryCode: "",
        companyCode: "",
        companyName: "",
        serviceCode: "",
        serviceName: "",
        branchCode: "",
        branchName: "",
        passbookName: ocrAccount.passbook_name,
        balance: ocrAccount.balance,
        authorizationStatus: 1,
        selectedStatus: 1,
        accountGroupId: accountGroupId,
        mlinkAccountId: ocrAccount.ocr_account_id,
        mlinkId: "",
        passbookType: "101",
        registeredDate: ocrAccount.registered_date,
        scrapeType: 0,
        ocrProcessId: ocrAccount.ocr_process_id,
        ocrProcessStatus: ocrAccount.ocr_process_status,
        otpYn: "0", // OCRはワンパス非対応のため0
        isUnderMaintenance: false,
      }));
      return accounts.filter((account) => account.selectedStatus === 1);
    }
    return undefined;
  };

  const getScrapFinances = async (accountGroupId: string): Promise<Accounts[] | undefined> => {
    const financeAccounts = await getScrapFinanceAccounts(accountGroupId);
      if (financeAccounts !== undefined) {
        const accounts = financeAccounts.map((finance) => ({
          industryCode: finance.industry_code,
          companyCode: finance.company_code,
          companyName: "",
          serviceCode: finance.service_code,
          serviceName: "",
          branchCode: finance.branch_code,
          branchName: "",
          passbookName: finance.passbook_name,
          balance: finance.last_balance !== null ? finance.last_balance.split(".")[0] : "", // Decimalのため小数点以下切り捨て
          authorizationStatus: 1,
          selectedStatus: 1,
          accountGroupId: accountGroupId,
          mlinkAccountId: finance.scrap_finance_account_id,
          mlinkId: finance.scrap_finance_id,
          passbookType: "09",
          registeredDate: finance.registered_date,
          scrapeType: 0,
          ocrProcessId: "",
          ocrProcessStatus: 0,
          otpYn: "0", // 銀行口座はワンパス非対応のため0
          isUnderMaintenance: false,
      }));
      return accounts.filter((account) => account.selectedStatus === 1);
    }
    return undefined;
  };

  const { getInstitutionsServices } = useInstitutionsServices();

  // 口座一覧からユニークな金融機関(industry_code + company_code)を抽出し、メンテナンス情報を取得する
  const fetchMaintenanceMap = async (accounts: Accounts[]): Promise<Map<string, boolean>> => {
    const maintenanceMap = new Map<string, boolean>();
    // ユニークな金融機関の組み合わせを抽出（industry_codeが空のものはメンテナンス対象外）
    const uniqueKeys = new Set<string>();
    for (const account of accounts) {
      if (account.industryCode === "") continue;
      const key = `${account.industryCode}_${account.companyCode}`;
      uniqueKeys.add(key);
    }

    const promises = Array.from(uniqueKeys).map(async (key) => {
      const [industryCode, companyCode] = key.split("_");
      try {
        const services = await getInstitutionsServices(industryCode, companyCode);
        if (!services) return;
        for (const service of services) {
          const serviceKey = `${service.industryCode}_${service.companyCode}_${service.serviceCode}`;
          // maintenance_type が 0 以外（1:メンテナンス中, 2:未契約）ならメンテナンス扱い
          maintenanceMap.set(serviceKey, Number(service.maintenanceType) !== 0);
        }
      } catch (error) {
        console.log("fetchMaintenanceMap:", error);
      }
    });

    await Promise.all(promises);
    return maintenanceMap;
  };

  // メンテナンス情報を口座データにマージする
  const applyMaintenance = (
    accounts: Accounts[],
    maintenanceMap: Map<string, boolean>,
  ): Accounts[] => {
    return accounts.map((account) => {
      const key = `${account.industryCode}_${account.companyCode}_${account.serviceCode}`;
      return {
        ...account,
        isUnderMaintenance: maintenanceMap.get(key) ?? false,
      };
    });
  };

  const getAllAccounts = async (accountGroupId: string) => {
    const requestId = ++latestGetAllAccountsRequestId.current;
    try {
      if (accountGroupId === "") {
        console.error("accountGroupIdなし");
        setError(
          new MoneyLinkWarning({
            title: "エラー",
            message: "口座グループが選択されていません。",
          }),
        );
        return;
      }

      const asyncTasks = [
        () => {if (selectedAccountGroup.getFinance) {return getFinances(selectedAccountGroup.accountGroupId)}},
        () => {if (selectedAccountGroup.getCreca) {return getCrecas(selectedAccountGroup.accountGroupId)}},
        () => {if (selectedAccountGroup.getPaymentCreca) {return getPayments(selectedAccountGroup.accountGroupId, "03")}},
        () => {if (selectedAccountGroup.getPaymentElectronicmoney) {return getPayments(selectedAccountGroup.accountGroupId, "10")}},
        () => {if (selectedAccountGroup.getEcshop) {return getEcshops(selectedAccountGroup.accountGroupId)}},
        () => {if (selectedAccountGroup.getOcr) {return getOcrs(selectedAccountGroup.accountGroupId)}},
        () => {if (selectedAccountGroup.getCsvexcel) {return getCsvExcels(selectedAccountGroup.accountGroupId)}},
        () => {if (selectedAccountGroup.getScrapFinance) {return getScrapFinances(selectedAccountGroup.accountGroupId)}},
      ];

      // 口座種別ごとに取得→メンテナンス情報取得→マージ→表示を独立して実行
      asyncTasks.forEach(async (task) => {
        try {
          const res = await task();
          if (res === undefined) return;
          if (requestId !== latestGetAllAccountsRequestId.current) return;

          // メンテナンス情報を取得してマージ
          const maintenanceMap = await fetchMaintenanceMap(res);
          if (requestId !== latestGetAllAccountsRequestId.current) return;

          const accountsWithMaintenance = applyMaintenance(res, maintenanceMap);
          setAccounts((prev) => {
            const next = [...prev, ...accountsWithMaintenance];
            next.sort((a, b) => a.registeredDate.localeCompare(b.registeredDate));
            return next;
          });
        } catch {
          // 個別の口座取得失敗は無視して他を表示し続ける
        }
      });
    } catch (error) {
      // エラーハンドリング
      console.log("getAllAccounts:", error);
    } finally {
      //
    }
  };

  return {
    openModal,
    setOpenModal,
    getFinances: errorHandledAsync(getFinances, setError),
    getCrecas: errorHandledAsync(getCrecas, setError),
    getPayments: errorHandledAsync(getPayments, setError),
    getEcshops: errorHandledAsync(getEcshops, setError),
    getOcrs: errorHandledAsync(getOcrs, setError),
    getCsvExcels: errorHandledAsync(getCsvExcels, setError),
    registerFinances: errorHandledAsync(registerFinances, setError),
    registerCrecas: errorHandledAsync(registerCrecas, setError),
    registerPayments: errorHandledAsync(registerPayments, setError),
    registerEcshops: errorHandledAsync(registerEcshops, setError),
    getAccountsToDisplay: errorHandledAsync(getAccountsToDisplay, setError),
    getAllAccounts: errorHandledAsync(getAllAccounts, setError),
  };
};
